import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiEndpoints } from "../../services/apiConfig";

// API Base
const API_URL = "/emails/add-custom-email-template";

// Async thunk - Save template
export const saveCustomEmailTemplate = createAsyncThunk(
  "customEmail/saveTemplate",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token"); // ✅ get token from localStorage

      const response = await axios.post(
        apiEndpoints.customeEmailTemplate,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`, // ✅ pass token in header
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error saving template");
    }
  }
);

export const createNewEmailTemplate = createAsyncThunk(
  "customEmail/createNewEmailTemplate",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token"); // ✅ get token from localStorage

      const response = await axios.post(
        apiEndpoints.createEmailTEmplate,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`, // ✅ pass token in header
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error saving template");
    }
  }
);

// Async thunk - Fetch template
export const fetchCustomEmailTemplate = createAsyncThunk(
  "customEmail/fetchTemplate",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_URL); // if GET is supported
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error fetching template");
    }
  }
);

export const checkSpam = createAsyncThunk(
  "customEmail/checkSpam",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        apiEndpoints.checkEmailSpamScore,
        payload, // 👈 preview content passed from button
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data; // e.g. { spam_score: 0.42, message: "Low spam" }
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error checking spam");
    }
  }
);

export const connectUserEmail = createAsyncThunk(
  "customEmail/connectUserEmail",
  async ({ provider, email, userId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const url = apiEndpoints.connectEmailInitiate
        .replace("{provider}", provider)
        .replace("{email}", email)
        .replace("{userId}", userId);

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (err) {
      /**
       * Handle ALL error cases safely
       */
      if (axios.isAxiosError(err)) {
        // Backend returned an error response
        if (err.response) {
          return rejectWithValue(
            err.response.data?.detail ||      // FastAPI style
            err.response.data?.message ||     // Common REST APIs
            "Something went wrong"
          );
        }

        // Request made but no response (network error)
        if (err.request) {
          return rejectWithValue("Network error. Please try again.");
        }
      }

      // Fallback (non-axios or unknown error)
      return rejectWithValue("Unexpected error occurred");
    }
  }
);


export const connectZohoProvider = createAsyncThunk(
  "emailConnect/conncetZohoProvide",
  async ({ provider, userId, email }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token") || "";

      const url = apiEndpoints.conncetZohoProvide
        .replace("{provider}", encodeURIComponent(provider))
        .replace("{userId}", encodeURIComponent(userId))
        .replace("{email}", encodeURIComponent(email));

      const res = await axios.get(url, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
      });

      // Expecting something like: { success:true, auth_url:"https://..." }
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to initiate connect"
      );
    }
  }
);

export const fetchAllEmailTemplates = createAsyncThunk(
  "emailTemplates/fetchEmailTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(apiEndpoints.getAllEmailTemplate, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.data; // assuming API response = { success: true, data: [...] }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch templates"
      );
    }
  }
);

export const fetchFollowUpEmailById = createAsyncThunk(
  "emailTemplates/fetchFollowUpEmailById",
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(apiEndpoints.getFollowUpEmailById.replaceAll("{id}",id), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.data; // assuming API response = { success: true, data: [...] }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch templates"
      );
    }
  }
);

export const updateCustomEmailTemplate = createAsyncThunk(
  "customEmail/updateCustomEmailTemplate",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        apiEndpoints.updateCustomEmailTemplate.replace("{id}", id),
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error updating template");
    }
  }
);

// Async thunk - Fetch template by ID
export const fetchCustomEmailTemplateById = createAsyncThunk(
  "customEmail/fetchTemplateById",
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        apiEndpoints.fetchCustomEmailTemplateById.replace("{id}", id),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error fetching template");
    }
  }
);

export const deleteCustomEmailTemplate = createAsyncThunk(
  "customEmail/deleteCustomEmailTemplate",
  async (templateId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        apiEndpoints.deleteCustomEmailTemplate,
        { template_id: templateId }, // 👈 backend expects this payload
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return { success: true, templateId };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error deleting template");
    }
  }
);

const customEmailSlice = createSlice({
  name: "customEmail",
  initialState: {
    subject_data: "",
    body_data: "",
    footer_data: "",
    spam_score: "",
    spam_analysis: {},
    connect_email: {},
    followUpEmailData:{},
    templates: [],
    loading: false,
    error: null,
    success: false,
  },
  reducers: {
    resetCustomEmailState: (state) => {
      state.subject_data = "";
      state.body_data = "";
      state.footer_data = "";
      state.spam_score = null;
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Save Template
      .addCase(saveCustomEmailTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(saveCustomEmailTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.subject_data = action.payload.subject_data;
        state.body_data = action.payload.body_data;
        state.footer_data = action.payload.footer_data;
        state.spam_score = action.payload.spam_score;
      })
      .addCase(saveCustomEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Template
      .addCase(fetchCustomEmailTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomEmailTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.subject_data = action.payload.subject_data;
        state.body_data = action.payload.body_data;
        state.footer_data = action.payload.footer_data;
        state.spam_score = action.payload.spam_score;
      })
      .addCase(fetchCustomEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(checkSpam.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkSpam.fulfilled, (state, action) => {
        state.loading = false;
        state.spam_analysis = action.payload.spam_analysis;

        // extract useful fields
        state.spam_score = action.payload.spam_analysis.spam_score;
        state.spam_message = action.payload.message;
      })
      .addCase(checkSpam.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(connectUserEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(connectUserEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.connect_email = action.payload;
      })
      .addCase(connectUserEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchAllEmailTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllEmailTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload;
      })
      .addCase(fetchAllEmailTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateCustomEmailTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCustomEmailTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // update state with latest template
        state.subject_data = action.payload.subject_data;
        state.body_data = action.payload.body_data;
        state.footer_data = action.payload.footer_data;
        state.spam_score = action.payload.spam_score;

        // also update list if it exists
        const idx = state.templates.findIndex(
          (t) => t.id === action.payload.id
        );
        if (idx !== -1) {
          state.templates[idx] = action.payload;
        }
      })
      .addCase(updateCustomEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCustomEmailTemplateById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomEmailTemplateById.fulfilled, (state, action) => {
        state.loading = false;
        const template = action.payload.data;

        state.subject_data = template.subject_data;
        state.body_data = template.body_data;
        state.footer_data = template.footer_data;
        state.spam_score = template.spam_score;
      })
      .addCase(fetchCustomEmailTemplateById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteCustomEmailTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCustomEmailTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // Remove deleted template from local state
        state.templates = state.templates.filter(
          (t) => t.id !== action.payload.templateId
        );
      })
      .addCase(deleteCustomEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(connectZohoProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(connectZohoProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.connect_email = action.payload;
        state.success = true;
      })
      .addCase(connectZohoProvider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      .addCase(fetchFollowUpEmailById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFollowUpEmailById.fulfilled, (state, action) => {
        state.loading = false;
        state.followUpEmailData = action.payload;
        state.success = true;
      })
      .addCase(fetchFollowUpEmailById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
    //   .addCase(connectUserEmail.pending, (state) => {
    //   state.loading = true;
    //   state.error = null;
    // })
    // .addCase(connectUserEmail.fulfilled, (state, action) => {
    //   state.loading = false;
    //   state.connect_email = action.payload;
    // })
    // .addCase(connectUserEmail.rejected, (state, action) => {
    //   state.loading = false;
    //   state.error = action.payload;
    // });
  },
});

export const { resetCustomEmailState } = customEmailSlice.actions;
export default customEmailSlice.reducer;
